import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import hero from "../../static/media/Home/Hero.webp";

const MotionButton = motion(Button);

const MotionText = React.memo(function MotionText({ text, isMobile }) {
  const [content, setContent] = useState(isMobile ? text : "");

  useEffect(() => {
    if (isMobile) {
      setContent(text);
      return;
    }

    let i = 0;
    setContent("");
    const interval = setInterval(() => {
      i += 1;
      setContent(text.slice(0, i));
      if (i >= text.length) clearInterval(interval);
    }, 35);

    return () => clearInterval(interval);
  }, [text, isMobile]);

  return (
    <Typography
      sx={{
        fontWeight: 700,
        letterSpacing: -0.2,
        fontSize: { xs: "clamp(18px,4.8vw,22px)", md: "clamp(20px,2.2vw,26px)" },
        textShadow: "0 14px 60px rgba(0,0,0,0.45)",
        background:
          "linear-gradient(90deg, rgba(229,9,20,1) 0%, rgba(255,62,74,1) 35%, rgba(180,0,16,1) 100%)",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
        minHeight: { xs: 28, md: 32 },
      }}
    >
      {content}
    </Typography>
  );
});

export default function HeroSection() {
  const fullTagline = useMemo(
    () => "Tu único rival eres tú. Supérate en cada entrenamiento.",
    []
  );
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(max-width:599.95px)");
    const handleChange = () => setIsMobile(media.matches);
    handleChange();
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  const whatsappLink =
    "https://wa.me/593985224301?text=Hola,%20quiero%20hablar%20con%20un%20asesor%20de%20NovaXFit.";

  return (
    <Box sx={{ width: "100%", bgcolor: "#000" }}>
      <Box
        component="section"
        sx={{
          width: "100%",
          position: "relative",
          overflow: "hidden",
          bgcolor: "#050505",
          minHeight: { xs: "60vh", md: "80vh" },
        }}
      >
        <Box
          component="img"
          src={hero}
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width={1600}
          height={1200}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: { xs: "center 30%", md: "center" },
            display: "block",
            transform: "translateZ(0)",
            userSelect: "none",
            WebkitUserDrag: "none",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.68) 46%, rgba(0,0,0,0.52) 100%)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(1100px 520px at 18% 38%, rgba(229,9,20,0.22), rgba(0,0,0,0) 58%)",
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(900px 520px at 80% 45%, rgba(0,0,0,0.08), rgba(0,0,0,0.52) 72%)",
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            width: "min(92vw, 1280px)",
            mx: "auto",
            position: "relative",
            zIndex: 1,
            display: "grid",
            gridTemplateColumns: "1fr",
            alignItems: "center",
            justifyItems: { xs: "center", md: "start" },
            pt: { xs: 6.25, sm: 7, md: 8 },
            pb: { xs: 5.75, sm: 6.5, md: 7.25 },
            minHeight: { xs: "74vh", sm: "72vh", md: "74vh" },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: 720, md: 760 },
              textAlign: { xs: "center", md: "left" },
              mx: { xs: "auto", md: 0 },
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontWeight: 700,
                color: "#ffffff",
                lineHeight: 0.95,
                fontSize: {
                  xs: "clamp(46px, 12vw, 68px)",
                  sm: "clamp(56px, 9vw, 80px)",
                  md: "clamp(70px, 5.6vw, 96px)",
                },
                letterSpacing: -1.2,
                textShadow: "0 18px 70px rgba(0,0,0,0.58)",
                display: "flex",
                alignItems: "baseline",
                justifyContent: { xs: "center", md: "flex-start" },
                flexWrap: "wrap",
                gap: "0.06em",
                mb: { xs: 1.5, md: 2.1 },
              }}
            >
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.78) 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                NOVA
              </Box>

              <Box
                component="span"
                aria-label="X"
                sx={{
                  position: "relative",
                  display: "inline-block",
                  px: { xs: 0.15, sm: 0.18 },
                  transform: "translateY(0.02em)",
                  background:
                    "linear-gradient(135deg, #ff3e4a 0%, #e50914 38%, #0a0a10 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  WebkitTextStroke: "2px rgba(255,255,255,0.92)",
                  textShadow: "0 26px 80px rgba(229,9,20,0.25)",
                }}
              >
                X
              </Box>

              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.78) 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                FIT
              </Box>
            </Typography>

            <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
              <MotionText text={fullTagline} isMobile={isMobile} />
            </Box>

            <Typography
              sx={{
                maxWidth: { xs: "100%", md: 700 },
                mx: { xs: "auto", md: 0 },
                color: "rgba(255,255,255,0.82)",
                lineHeight: 1.75,
                fontSize: { xs: 15.5, sm: 16.5, md: 17.5 },
                fontWeight: 500,
                textShadow: "0 16px 70px rgba(0,0,0,0.45)",
                mb: { xs: 2.4, md: 3.2 },
              }}
            >
              En{" "}
              <Box
                component="strong"
                sx={{ fontWeight: 800, color: "rgba(255,255,255,0.94)" }}
              >
                NovaXFit
              </Box>
              , cada sesión cuenta. Entrena en un espacio diseñado para ayudarte
              a ganar fuerza, disciplina y resultados reales con constancia.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.2}
              sx={{
                justifyContent: { xs: "center", md: "flex-start" },
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <MotionButton
                component={RouterLink}
                to="/contacto"
                variant="contained"
                fullWidth
                initial={{ opacity: 0, y: 0, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "16px",
                  py: { xs: 1.15, sm: 1.2 },
                  px: { xs: 2.75, sm: 3.1 },
                  fontSize: { xs: "0.88rem", sm: "0.92rem", md: "1rem" },
                  minHeight: 46,
                  color: "#fff",
                  bgcolor: "#e50914",
                  boxShadow: "0 18px 60px rgba(229,9,20,0.26)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  "&:hover": {
                    bgcolor: "#ff2430",
                  },
                  width: { sm: "auto" },
                }}
              >
                Contáctanos
              </MotionButton>

              <MotionButton
                component="a"
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                fullWidth
                initial={{ opacity: 0, y: 0, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.32, ease: "easeOut" }}
                sx={{
                  borderColor: "rgba(255,255,255,0.78)",
                  color: "#fff",
                  fontWeight: 700,
                  px: { xs: 2.75, sm: 3.1 },
                  py: { xs: 1.15, sm: 1.2 },
                  fontSize: { xs: "0.88rem", sm: "0.92rem", md: "1rem" },
                  borderRadius: "16px",
                  minHeight: 46,
                  "&:hover": {
                    borderColor: "#e50914",
                    bgcolor: "rgba(229,9,20,0.14)",
                    color: "#fff",
                  },
                  width: { sm: "auto" },
                }}
              >
                Habla con un asesor
              </MotionButton>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 22,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 100%)",
            pointerEvents: "none",
          }}
        />
      </Box>
    </Box>
  );
}